import { useState, type FormEvent } from 'react'
import { useCreateClient } from '@/api/queries'
import { ApiError } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { Field, Input, Select } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Overlay'
import { RepeatableList } from '@/features/client-profile/RepeatableList'
import { CADENCES } from '@/lib/constants'
import type { Client, CrawlCadence } from '@/types'

interface Draft {
  id: string
  name: string
  primaryDomain: string
  additionalDomains: string[]
  pageLimit: string
  excludePatterns: string[]
  cadence: CrawlCadence | ''
}

const EMPTY: Draft = {
  id: '',
  name: '',
  primaryDomain: '',
  additionalDomains: [],
  pageLimit: '10000',
  excludePatterns: [],
  cadence: '',
}

type Errors = Partial<Record<'id' | 'name' | 'primaryDomain' | 'pageLimit', string>>

function positiveInt(value: string): number | null {
  const n = Number(value.trim())
  return value.trim() && Number.isInteger(n) && n >= 1 ? n : null
}

function clean(values: string[]): string[] {
  return values.map((v) => v.trim()).filter(Boolean)
}

/**
 * Temporary (APP-71): creates a client for testing and setup. Shown only when
 * `config.json` sets `features.clientCreate`, which the deploy does for
 * testing alone. Asks only for what starting a crawl needs; the rest is on
 * Client Profile afterwards.
 */
export function NewClientModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (client: Client) => void
}) {
  const create = useCreateClient()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [errors, setErrors] = useState<Errors>({})
  const [serverError, setServerError] = useState<string | null>(null)

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }))

  function close() {
    setDraft(EMPTY)
    setErrors({})
    setServerError(null)
    onClose()
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const id = positiveInt(draft.id)
    const pageLimit = positiveInt(draft.pageLimit)
    const next: Errors = {}
    if (id === null) next.id = 'A whole number, 1 or more.'
    if (!draft.name.trim()) next.name = 'Required.'
    if (!draft.primaryDomain.trim()) next.primaryDomain = 'Required.'
    if (pageLimit === null) next.pageLimit = 'A whole number, 1 or more.'
    setErrors(next)
    setServerError(null)
    if (id === null || pageLimit === null || Object.keys(next).length) return

    try {
      const client = await create.mutateAsync({
        id,
        name: draft.name.trim(),
        primaryDomain: draft.primaryDomain.trim(),
        additionalDomains: clean(draft.additionalDomains),
        excludePatterns: clean(draft.excludePatterns),
        pageLimit,
        cadence: draft.cadence || null,
      })
      setDraft(EMPTY)
      onCreated(client)
    } catch (cause) {
      setServerError(cause instanceof ApiError ? cause.message : 'The client was not created.')
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="New client"
      description="For testing and setup only. Identity and crawl scope can be changed on Client Profile afterwards."
      width="max-w-[560px]"
      footer={
        <>
          <Button size="sm" onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" type="submit" form="new-client" loading={create.isPending}>
            Create client
          </Button>
        </>
      }
    >
      <form id="new-client" noValidate onSubmit={(e) => void submit(e)} className="space-y-5">
        {serverError && (
          <p role="alert" className="rounded-[4px] bg-brand-red-50 px-3 py-2 text-[13px] leading-5 text-brand-red-700">
            {serverError}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-[140px_1fr]">
          <Field label="Client id" required error={errors.id} hint="Upstream system id.">
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={draft.id}
                className="tabular"
                onChange={(e) => set({ id: e.target.value })}
              />
            )}
          </Field>
          <Field label="Client name" required error={errors.name}>
            {(p) => <Input {...p} value={draft.name} onChange={(e) => set({ name: e.target.value })} />}
          </Field>
        </div>

        <Field label="Primary domain" required error={errors.primaryDomain} hint="Hostname only, no scheme. The crawl starts here.">
          {(p) => (
            <Input
              {...p}
              value={draft.primaryDomain}
              placeholder="example.edu"
              className="font-mono text-[13px]"
              onChange={(e) => set({ primaryDomain: e.target.value })}
            />
          )}
        </Field>

        <RepeatableList
          label="Additional domains and subdomains"
          values={draft.additionalDomains}
          onChange={(additionalDomains) => set({ additionalDomains })}
          placeholder="catalog.example.edu"
          addLabel="Add domain"
          mono
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Page limit" required error={errors.pageLimit} hint="Most pages one crawl may fetch.">
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={draft.pageLimit}
                className="tabular"
                onChange={(e) => set({ pageLimit: e.target.value })}
              />
            )}
          </Field>
          <Field label="Re-crawl cadence">
            {(p) => (
              <Select
                {...p}
                value={draft.cadence}
                onChange={(e) => set({ cadence: e.target.value as CrawlCadence | '' })}
              >
                <option value="">Not set</option>
                {CADENCES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <RepeatableList
          label="Exclude keywords"
          values={draft.excludePatterns}
          onChange={(excludePatterns) => set({ excludePatterns })}
          placeholder="archive"
          addLabel="Add keyword"
          mono
          hint="A URL containing any of these is not crawled. Plain text, not a glob or a regex."
        />
      </form>
    </Modal>
  )
}
